import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertCircle, CheckCircle, Loader2, Clock, Plus, Sparkles, Truck, ShoppingBag, Heart } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Navbar } from '../components/custom/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { orderAPI, paymentAPI, deliveryAPI } from '../utils/api';
import { toast } from 'sonner';
import axios from 'axios';
import { SEO } from '../seo/SEO';
import * as meta from '../analytics/meta';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { cartItems, getTotalPrice, clearCart, addToCart } = useCart();
  const { user } = useAuth();
  const [orderType, setOrderType] = useState('delivery'); // 'delivery' | 'collection'
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('card'); // card or cod
  // Fire Meta Pixel `AddPaymentInfo` exactly once per checkout session,
  // the first time the customer actively selects a payment method.
  // (Initial 'card' default doesn't count — it's not a user action.)
  const paymentInfoFiredRef = React.useRef(false);
  const handleSelectPaymentMethod = React.useCallback((method) => {
    setPaymentMethod(method);
    if (!paymentInfoFiredRef.current) {
      paymentInfoFiredRef.current = true;
      meta.track('AddPaymentInfo', {
        content_category: method,
        currency: 'GBP',
      });
    }
  }, []);
  const [tipSelection, setTipSelection] = useState(0); // 0 | 1 | 2 | 5 | 'custom'
  const [customTip, setCustomTip] = useState('');
  const [suggestedProducts, setSuggestedProducts] = useState([]);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: '',
    house_no: '',
    address: '',
    postcode: '',
    notes: ''
  });

  // Restaurant status state
  const [restaurantStatus, setRestaurantStatus] = useState({
    status: 'open',
    is_open: true,
    can_checkout: true,
    is_preorder: false,
    message: '',
    current_time_uk: '',
    opens_at: null,
    loading: true
  });

  // Postcode validation state
  const [postcodeValidation, setPostcodeValidation] = useState({
    checking: false,
    checked: false,
    deliverable: false,
    delivery_fee: null,
    minimum_order: null,
    message: ''
  });

  const subtotal = getTotalPrice();
  const serviceCharge = 0.75; // Fixed service charge on all orders
  const deliveryFee = orderType === 'delivery' && postcodeValidation.deliverable
    ? (postcodeValidation.delivery_fee || 0)
    : 0;
  const tip = tipSelection === 'custom'
    ? Math.max(0, Math.round((parseFloat(customTip) || 0) * 100) / 100)
    : tipSelection;
  const total = subtotal + serviceCharge + deliveryFee + tip;

  // Fetch restaurant status on mount
  useEffect(() => {
    const checkRestaurantStatus = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/restaurant/status`);
        setRestaurantStatus({
          ...response.data,
          loading: false
        });
      } catch (error) {
        console.error('Failed to check restaurant status:', error);
        // Default to open if status check fails
        setRestaurantStatus(prev => ({
          ...prev,
          loading: false
        }));
      }
    };

    checkRestaurantStatus();
    
    // Refresh status every minute
    const interval = setInterval(checkRestaurantStatus, 60000);
    return () => clearInterval(interval);
  }, []);

  // Fetch suggested products
  useEffect(() => {
    const fetchSuggested = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/suggested-products`);
        setSuggestedProducts(response.data);
      } catch (error) {
        console.error('Failed to fetch suggested products:', error);
      }
    };
    fetchSuggested();
  }, []);

  // Filter out items already in cart
  const cartItemIds = cartItems.map(item => item.item_id);
  const filteredSuggestions = suggestedProducts.filter(
    s => !cartItemIds.includes(s.menu_item?.id)
  );

  const handleAddSuggested = (item) => {
    addToCart({
      item_id: item.id,
      name: item.name,
      price: item.price,
      quantity: 1,
      image_url: item.image_url,
      customizations: {}
    });
    toast.success(`${item.name} added to cart`);
  };

  // Debounced postcode validation
  const validatePostcode = useCallback(async (postcode) => {
    if (!postcode || postcode.length < 5) {
      setPostcodeValidation({
        checking: false,
        checked: false,
        deliverable: false,
        delivery_fee: null,
        minimum_order: null,
        message: ''
      });
      return;
    }

    setPostcodeValidation(prev => ({ ...prev, checking: true }));

    try {
      const response = await deliveryAPI.validatePostcode(postcode);
      setPostcodeValidation({
        checking: false,
        checked: true,
        deliverable: response.data.deliverable,
        delivery_fee: response.data.delivery_fee,
        minimum_order: response.data.minimum_order,
        message: response.data.message
      });
    } catch (error) {
      console.error('Postcode validation error:', error);
      setPostcodeValidation({
        checking: false,
        checked: true,
        deliverable: false,
        delivery_fee: null,
        minimum_order: null,
        message: 'Failed to validate postcode. Please try again.'
      });
    }
  }, []);

  // Validate postcode when it changes (with debounce) — only for delivery orders.
  useEffect(() => {
    if (orderType !== 'delivery') return;
    const timer = setTimeout(() => {
      validatePostcode(formData.postcode);
    }, 500);

    return () => clearTimeout(timer);
  }, [formData.postcode, orderType, validatePostcode]);

  const canProceed = () => {
    // Restaurant must allow checkout (open or preorder)
    if (!restaurantStatus.can_checkout) return false;
    // Collection orders skip postcode/min-order requirements.
    if (orderType === 'collection') return true;
    // Delivery orders must pass postcode validation + minimum order
    if (!postcodeValidation.checked) return false;
    if (!postcodeValidation.deliverable) return false;
    if (postcodeValidation.minimum_order && subtotal < postcodeValidation.minimum_order) {
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Re-check restaurant status before submitting
    if (!restaurantStatus.can_checkout) {
      toast.error(restaurantStatus.message || 'Restaurant is closed');
      return;
    }
    
    if (cartItems.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    if (orderType === 'delivery') {
      if (!formData.house_no || !formData.house_no.trim()) {
        toast.error('Please enter your house / flat number');
        return;
      }
      if (!formData.address || !formData.address.trim()) {
        toast.error('Please enter delivery address');
        return;
      }
      if (!formData.postcode || !formData.postcode.trim()) {
        toast.error('Please enter your post code');
        return;
      }
      if (!postcodeValidation.deliverable) {
        toast.error("Sorry, we don't deliver to this postcode");
        return;
      }
      if (postcodeValidation.minimum_order && subtotal < postcodeValidation.minimum_order) {
        toast.error(`Minimum order for delivery is £${postcodeValidation.minimum_order.toFixed(2)}`);
        return;
      }
    }

    setLoading(true);
    try {
      const fullAddress = orderType === 'delivery' 
        ? `${formData.house_no.trim()}, ${formData.address.trim()}, ${formData.postcode.toUpperCase().trim()}`
        : null;

      const orderData = {
        customer_name: formData.name,
        customer_email: formData.email,
        customer_phone: formData.phone,
        items: cartItems,
        total_amount: total,
        subtotal,
        service_charge: serviceCharge,
        delivery_fee: deliveryFee,
        tip: tip > 0 ? tip : 0,
        order_type: orderType,
        delivery_address: fullAddress,
        postcode: orderType === 'delivery' ? formData.postcode.toUpperCase().trim() : null,
        payment_method: paymentMethod,
        notes: formData.notes || null,
        user_id: user?.id || null
      };

      if (paymentMethod === 'cod') {
        // Cash on Delivery - create order directly
        const orderRes = await axios.post(`${API_URL}/api/orders/cod`, orderData);
        const order = orderRes.data;
        clearCart();
        // Navigate to success page with order details
        navigate(`/order-success?order_id=${order.id}&cod=true`);
      } else {
        // Card payment - use Stripe
        const orderRes = await orderAPI.createOrder(orderData);
        const order = orderRes.data;

        const paymentData = {
          order_id: order.id,
          amount: total,
          origin_url: window.location.origin
        };

        const paymentRes = await paymentAPI.createCheckoutSession(paymentData);
        clearCart();
        window.location.href = paymentRes.data.url;
      }
    } catch (error) {
      console.error('Checkout failed:', error);
      const message = error.response?.data?.detail || 'Failed to process order. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-2xl mx-auto px-4 py-12 text-center">
          <h2 className="text-2xl font-bold mb-4">Your cart is empty</h2>
          <Button onClick={() => navigate('/')} className="rounded-full">
            Browse Menu
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SEO title="Checkout — Cravin Crrispy" noIndex includeRestaurantSchema={false} />
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 py-8">
        <button 
          onClick={() => navigate(-1)}
          className="flex items-center text-muted-foreground hover:text-foreground mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </button>

        <h1 className="text-3xl font-bold mb-8">Checkout</h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Restaurant Status Banners */}
          {!restaurantStatus.loading && (
            <>
              {/* CLOSED - Red banner */}
              {restaurantStatus.status === 'closed' && (
                <div className="bg-red-50 border border-red-300 rounded-2xl p-4 shadow-sm">
                  <div className="flex items-center gap-3 text-red-800">
                    <AlertCircle className="h-5 w-5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold">Restaurant is closed</p>
                      <p className="text-sm mt-1">Preorders open after 11:00 AM.</p>
                      <p className="text-xs mt-1 text-red-600">Current UK time: {restaurantStatus.current_time_uk}</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* PREORDER - Orange banner */}
              {restaurantStatus.status === 'preorder' && (
                <div className="bg-orange-50 border border-orange-300 rounded-2xl p-4 shadow-sm">
                  <div className="flex items-center gap-3 text-orange-800">
                    <Clock className="h-5 w-5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold">Preorder only</p>
                      <p className="text-sm mt-1">We open at 4:00 PM. Your order will be processed after we open (between 4:00 PM and 11:00 PM).</p>
                      <p className="text-xs mt-1 text-orange-600">Current UK time: {restaurantStatus.current_time_uk}</p>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Preparation Time Notice - only show when can checkout (open or preorder) */}
          {restaurantStatus.can_checkout && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 shadow-sm">
              <div className="flex items-center gap-3 text-amber-800">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
                <span className="font-medium">Estimated preparation time: <strong data-testid="checkout-prep-time">{
                  orderType === 'collection'
                    ? `${restaurantStatus.collection_time ?? 30} minutes`
                    : `${restaurantStatus.preparation_time ?? 60} minutes`
                }</strong></span>
              </div>
            </div>
          )}

          {/* Order Type Toggle: Delivery / Collection */}
          <div className="bg-white rounded-2xl p-4 shadow-sm" data-testid="order-type-toggle">
            <h2 className="font-bold text-lg mb-3">How would you like your order?</h2>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                data-testid="order-type-delivery"
                onClick={() => setOrderType('delivery')}
                className={`flex flex-col items-center justify-center gap-1.5 rounded-xl border-2 p-4 transition-all ${
                  orderType === 'delivery'
                    ? 'border-primary bg-primary/5 text-primary'
                    : 'border-border bg-white text-foreground hover:border-muted-foreground'
                }`}
              >
                <Truck className="h-6 w-6" />
                <span className="font-semibold text-sm">Delivery</span>
                <span className="text-xs opacity-70">Bring it to me</span>
              </button>
              <button
                type="button"
                data-testid="order-type-collection"
                onClick={() => setOrderType('collection')}
                className={`flex flex-col items-center justify-center gap-1.5 rounded-xl border-2 p-4 transition-all ${
                  orderType === 'collection'
                    ? 'border-primary bg-primary/5 text-primary'
                    : 'border-border bg-white text-foreground hover:border-muted-foreground'
                }`}
              >
                <ShoppingBag className="h-6 w-6" />
                <span className="font-semibold text-sm">Collection</span>
                <span className="text-xs opacity-70">I&apos;ll pick it up</span>
              </button>
            </div>
          </div>

          {/* Customer Details */}
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <h2 className="font-bold text-lg mb-4">Your Details</h2>
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Full Name</Label>
                <Input
                  data-testid="name-input"
                  id="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  data-testid="email-input"
                  id="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  data-testid="phone-input"
                  id="phone"
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="mt-1.5"
                />
              </div>

              {orderType === 'delivery' && (
                <>
                  <div>
                    <Label htmlFor="house_no">
                      House / Flat No. <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      data-testid="house-no-input"
                      id="house_no"
                      required
                      value={formData.house_no}
                      onChange={(e) => setFormData({...formData, house_no: e.target.value})}
                      className="mt-1.5"
                      placeholder="e.g., 25 / Flat 2B"
                    />
                  </div>
                  <div>
                    <Label htmlFor="address">
                      Delivery Address <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      data-testid="address-input"
                      id="address"
                      required
                      value={formData.address}
                      onChange={(e) => setFormData({...formData, address: e.target.value})}
                      className="mt-1.5"
                      placeholder="Street name (e.g., Airlie Crescent)"
                    />
                  </div>
                  <div>
                    <Label htmlFor="postcode">
                      Post Code <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <Input
                        data-testid="postcode-input"
                        id="postcode"
                        required
                        value={formData.postcode}
                        onChange={(e) => setFormData({...formData, postcode: e.target.value.toUpperCase()})}
                        className={`mt-1.5 uppercase pr-10 ${
                          postcodeValidation.checked && !postcodeValidation.deliverable
                            ? 'border-red-500 focus:ring-red-500'
                            : postcodeValidation.checked && postcodeValidation.deliverable
                              ? 'border-green-500'
                              : ''
                        }`}
                        placeholder="e.g., DD8 1AB"
                        maxLength={8}
                      />
                      {postcodeValidation.checking && (
                        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground mt-0.5" />
                      )}
                      {postcodeValidation.checked && postcodeValidation.deliverable && (
                        <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500 mt-0.5" />
                      )}
                      {postcodeValidation.checked && !postcodeValidation.deliverable && (
                        <AlertCircle className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-red-500 mt-0.5" />
                      )}
                    </div>
                    
                    {/* Postcode validation message */}
                    <AnimatePresence>
                      {postcodeValidation.checked && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className={`mt-2 p-3 rounded-lg text-sm ${
                            postcodeValidation.deliverable 
                              ? 'bg-green-50 text-green-700' 
                              : 'bg-red-50 text-red-700'
                          }`}
                        >
                          {postcodeValidation.deliverable ? (
                            <div className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4" />
                              <span>
                                Delivery available! Fee: <strong>£{postcodeValidation.delivery_fee?.toFixed(2)}</strong>
                                {postcodeValidation.minimum_order && (
                                  <> | Min order: <strong>£{postcodeValidation.minimum_order.toFixed(2)}</strong></>
                                )}
                              </span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <AlertCircle className="h-4 w-4" />
                              <span>{postcodeValidation.message}</span>
                            </div>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {/* Minimum order warning */}
                    {postcodeValidation.deliverable && postcodeValidation.minimum_order && subtotal < postcodeValidation.minimum_order && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="mt-2 p-3 rounded-lg text-sm bg-orange-50 text-orange-700"
                      >
                        <AlertCircle className="h-4 w-4 inline mr-2" />
                        Add £{(postcodeValidation.minimum_order - subtotal).toFixed(2)} more to meet minimum order for delivery
                      </motion.div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Order Notes */}
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <h2 className="font-bold text-lg mb-4">Order Notes</h2>
            <div>
              <Label htmlFor="notes">Special Instructions (Optional)</Label>
              <textarea
                data-testid="notes-input"
                id="notes"
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                className="mt-1.5 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                placeholder="Any special requests or delivery instructions..."
              />
            </div>
          </div>

          {/* Payment Method Selection */}
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <h2 className="font-bold text-lg mb-4">Payment Method</h2>
            <div className="space-y-3">
              {/* Card Payment Option */}
              <label 
                data-testid="payment-card-option"
                className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  paymentMethod === 'card' 
                    ? 'border-primary bg-primary/5' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <input
                  type="radio"
                  name="paymentMethod"
                  value="card"
                  checked={paymentMethod === 'card'}
                  onChange={(e) => handleSelectPaymentMethod(e.target.value)}
                  className="sr-only"
                />
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  paymentMethod === 'card' ? 'border-primary' : 'border-gray-300'
                }`}>
                  {paymentMethod === 'card' && (
                    <div className="w-3 h-3 rounded-full bg-primary" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect width="20" height="14" x="2" y="5" rx="2" />
                      <line x1="2" x2="22" y1="10" y2="10" />
                    </svg>
                    <span className="font-medium">Pay with Card</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Secure payment via Stripe
                  </p>
                </div>
              </label>

              {/* Cash on Delivery Option */}
              <label 
                data-testid="payment-cod-option"
                className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  paymentMethod === 'cod' 
                    ? 'border-primary bg-primary/5' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <input
                  type="radio"
                  name="paymentMethod"
                  value="cod"
                  checked={paymentMethod === 'cod'}
                  onChange={(e) => handleSelectPaymentMethod(e.target.value)}
                  className="sr-only"
                />
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  paymentMethod === 'cod' ? 'border-primary' : 'border-gray-300'
                }`}>
                  {paymentMethod === 'cod' && (
                    <div className="w-3 h-3 rounded-full bg-primary" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="8" cy="8" r="6" />
                      <path d="M18.09 10.37A6 6 0 1 1 10.34 18" />
                      <path d="M7 6h1v4" />
                      <path d="m16.71 13.88.7.71-2.82 2.82" />
                    </svg>
                    <span className="font-medium">Cash on Delivery</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Pay when you receive your order
                  </p>
                </div>
              </label>
            </div>
          </div>

          {/* Tip Section */}
          <div className="bg-white rounded-2xl p-6 shadow-sm" data-testid="tip-section">
            <div className="flex items-center gap-2 mb-1">
              <Heart className="h-5 w-5 text-primary" />
              <h2 className="font-bold text-lg">Add a Tip</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Show your appreciation to the team (optional)
            </p>
            <div className="flex flex-wrap gap-2">
              {[
                { value: 0, label: 'No tip' },
                { value: 1, label: '£1' },
                { value: 2, label: '£2' },
                { value: 5, label: '£5' },
                { value: 'custom', label: 'Custom' },
              ].map(opt => (
                <button
                  key={String(opt.value)}
                  type="button"
                  data-testid={`tip-option-${String(opt.value)}`}
                  onClick={() => setTipSelection(opt.value)}
                  className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                    tipSelection === opt.value
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-white text-foreground border-gray-200 hover:border-primary'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {tipSelection === 'custom' && (
              <div className="mt-3 flex items-center gap-2 max-w-[200px]">
                <span className="text-lg font-semibold">£</span>
                <Input
                  type="number"
                  min="0"
                  step="0.50"
                  placeholder="0.00"
                  value={customTip}
                  onChange={(e) => setCustomTip(e.target.value)}
                  data-testid="tip-custom-input"
                />
              </div>
            )}
          </div>

          {/* Order Summary */}
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <h2 className="font-bold text-lg mb-4">Order Summary</h2>
            <div className="space-y-3">
              {cartItems.map((item, index) => (
                <div key={item.id || item.cartId || `${item.name || 'item'}-${index}`} className="flex justify-between text-sm">
                  <span>{item.quantity}x {item.name}</span>
                  <span>£{(item.price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
              <div className="border-t pt-3">
                <div className="flex justify-between text-sm">
                  <span>Subtotal</span>
                  <span>£{subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span>Service Charge</span>
                  <span>£{serviceCharge.toFixed(2)}</span>
                </div>
                {orderType === 'delivery' && postcodeValidation.deliverable && (
                  <div className="flex justify-between text-sm mt-1">
                    <span>Delivery Fee</span>
                    <span>£{deliveryFee.toFixed(2)}</span>
                  </div>
                )}
                {orderType === 'collection' && (
                  <div className="flex justify-between text-sm mt-1 text-muted-foreground">
                    <span>Collection</span>
                    <span>Free</span>
                  </div>
                )}
                {tip > 0 && (
                  <div className="flex justify-between text-sm mt-1" data-testid="tip-summary-line">
                    <span>Tip</span>
                    <span>£{tip.toFixed(2)}</span>
                  </div>
                )}
              </div>
              <div className="flex justify-between font-bold text-lg pt-2 border-t">
                <span>Total</span>
                <span className="text-primary">£{total.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Suggested Products */}
          {filteredSuggestions.length > 0 && (
            <div className="bg-gradient-to-r from-primary/5 to-primary/10 rounded-2xl p-6 shadow-sm" data-testid="suggested-products-checkout">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="h-5 w-5 text-primary" />
                <h2 className="font-bold text-lg">Complete Your Order</h2>
              </div>
              <p className="text-sm text-muted-foreground mb-4">Add these popular items before you checkout!</p>
              <div className="space-y-3">
                {filteredSuggestions.map((suggestion) => (
                  <motion.div
                    key={suggestion.suggestion_id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center justify-between bg-white rounded-xl p-4 shadow-sm"
                    data-testid="suggested-product-checkout-item"
                  >
                    <div className="flex-1 min-w-0 mr-4">
                      <h4 className="font-semibold text-sm">{suggestion.menu_item?.name}</h4>
                      <p className="text-xs text-muted-foreground truncate">{suggestion.menu_item?.description}</p>
                      <p className="text-sm font-bold text-primary mt-1">£{suggestion.menu_item?.price?.toFixed(2)}</p>
                    </div>
                    <Button
                      size="sm"
                      className="rounded-full"
                      onClick={() => handleAddSuggested(suggestion.menu_item)}
                      data-testid="add-suggested-checkout-button"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add
                    </Button>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* Payment Button - Fixed spacing for mobile */}
          <div className="pb-24 sm:pb-8">
            {/* Show closed message near button when CLOSED */}
            {restaurantStatus.status === 'closed' && !restaurantStatus.loading && (
              <div className="mb-4 p-3 bg-red-100 border border-red-300 rounded-xl text-center">
                <p className="text-red-800 font-medium text-sm">
                  Restaurant is closed. Preorders open after 11:00 AM (UK time)
                </p>
              </div>
            )}
            
            <Button
              data-testid="proceed-to-payment-button"
              type="submit"
              className={`w-full h-14 text-lg rounded-full ${
                restaurantStatus.status === 'closed' ? 'bg-gray-400 cursor-not-allowed' : ''
              }`}
              disabled={loading || !canProceed() || restaurantStatus.loading}
            >
              {restaurantStatus.loading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  Checking availability...
                </>
              ) : loading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  Processing...
                </>
              ) : restaurantStatus.status === 'closed' ? (
                'Restaurant Closed'
              ) : !canProceed() ? (
                orderType === 'collection'
                  ? 'Restaurant unavailable'
                  : postcodeValidation.checking ? 'Checking postcode...' :
                    !postcodeValidation.checked ? 'Enter valid postcode' :
                    !postcodeValidation.deliverable ? "Can't deliver to this postcode" :
                    `Add £${(postcodeValidation.minimum_order - subtotal).toFixed(2)} more`
              ) : restaurantStatus.is_preorder ? (
                paymentMethod === 'cod' 
                  ? `Place Preorder (${orderType === 'collection' ? 'Pay on Collection' : 'Cash on Delivery'}) - £${total.toFixed(2)}`
                  : `Place Preorder - £${total.toFixed(2)}`
              ) : (
                paymentMethod === 'cod'
                  ? `Place Order (${orderType === 'collection' ? 'Pay on Collection' : 'Cash on Delivery'}) - £${total.toFixed(2)}`
                  : `Proceed to Payment - £${total.toFixed(2)}`
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CheckoutPage;
